import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommissionService, DistributorCommissionPolicy } from '../../services';

export interface CommissionPlanSelection {
  policyId: string;
  planId: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-commission-policy-plan-picker',
  templateUrl: './commission-policy-plan-picker.component.html',
  standalone: false,
})
export class CommissionPolicyPlanPickerComponent implements OnChanges {
  private commissionSvc = inject(CommissionService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  @Input() distributorId: string | null = null;
  @Output() planSelected = new EventEmitter<CommissionPlanSelection | null>();

  protected loading = false;
  protected error = false;
  protected policies: DistributorCommissionPolicy[] = [];
  protected selectedPolicyId: string | null = null;
  protected selectedPlanId: string | null = null;

  private requestGeneration = 0;

  protected get selectedPolicy(): DistributorCommissionPolicy | null {
    return this.policies.find(policy => policy.id === this.selectedPolicyId) ?? null;
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (!changes['distributorId']) return;

    const generation = ++this.requestGeneration;

    this.selectedPolicyId = null;
    this.selectedPlanId = null;
    this.error = false;
    this.policies = [];
    this.planSelected.emit(null);

    if (!this.distributorId) {
      this.loading = false;
      return;
    }

    this.loading = true;
    try {
      const response = await this.commissionSvc.listPolicies(this.distributorId);
      if (generation !== this.requestGeneration) return; // a newer distributorId superseded this call
      this.policies = response.items;
    } catch (err) {
      if (generation !== this.requestGeneration) return;
      this.error = true;
      console.error('Failed to load commission-eligible policies', err);
    } finally {
      // Parent page is OnPush and won't pick up these async mutations on its own — mark dirty manually.
      if (generation === this.requestGeneration) {
        this.loading = false;
        this.changeDetectorRef.markForCheck();
      }
    }
  }

  protected onPolicyChange(policyId: string | readonly string[] | null) {
    this.selectedPolicyId = typeof policyId === 'string' ? policyId : null;
    this.selectedPlanId = null;
    this.planSelected.emit(null);
  }

  protected onPlanChange(planId: string | readonly string[] | null) {
    const resolvedPlanId = typeof planId === 'string' ? planId : null;
    this.selectedPlanId = resolvedPlanId;

    if (this.selectedPolicyId && resolvedPlanId) {
      this.planSelected.emit({ policyId: this.selectedPolicyId, planId: resolvedPlanId });
    } else {
      this.planSelected.emit(null);
    }
  }
}
